import { ConditionGroup } from '@hogwarts/conditionals';
import React, { forwardRef, useContext, useImperativeHandle } from 'react';
import ConditionalsForScheme, { ConditionalScheme } from '../../../../components/Conditionals';

export type ReportColorKey = 'green' | 'amber' | 'red';

export interface ReportColorRules {
  green?: ConditionGroup;
  amber?: ConditionGroup;
  red?: ConditionGroup;
}

export interface ReportColorsPanelContextValue {
  reportColorRules?: ReportColorRules;
  onChange: (color: ReportColorKey, condition: ConditionGroup) => void;
}

export const ReportColorsPanelOnChangeContext =
  React.createContext<ReportColorsPanelContextValue | null>(null);

const COLOR_LABELS: Record<ReportColorKey, string> = {
  green: 'Green when…',
  amber: 'Amber when…',
  red: 'Red when…',
};

const ReportColorsPanel = forwardRef((_props: any, ref: any) => {
  useImperativeHandle(ref, () => ({
    getReactContainerClasses() {
      return ['w-100'];
    },
  }));

  const scheme = useContext(ConditionalScheme);
  const context = useContext(ReportColorsPanelOnChangeContext);

  if (!scheme) {
    return (
      <div className="p-3 w-100">
        <p>Scheme not provided.</p>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="p-3 w-100">
        <p>Report Colors panel not connected.</p>
      </div>
    );
  }

  const { reportColorRules = {}, onChange } = context;

  return (
    <div className="p-3 w-100 h-100" style={{ overflow: 'auto' }}>
      <p className="mb-3 text-muted small">
        Define when cells should be coloured green, amber or red in reports
        (e.g. &quot;Field A on or before Start Date&quot; → green; &quot;Field
        A more than 7 days after Start Date&quot; → red).
      </p>
      {(['green', 'amber', 'red'] as ReportColorKey[]).map((color) => (
        <div key={color} className="mb-4">
          <h6 className="mb-2" style={{ fontWeight: 600 }}>
            {COLOR_LABELS[color]}
          </h6>
          <ConditionalsForScheme
            scheme={scheme}
            condition={reportColorRules[color]}
            readOnly={false}
            onChange={(condition) => onChange(color, condition)}
          />
        </div>
      ))}
    </div>
  );
});

ReportColorsPanel.displayName = 'ReportColorsPanel';

export default ReportColorsPanel;
